import { Reveal, Overline } from "./Reveal";
import { Newspaper, ArrowUpRight, Phone, Mail, MapPin } from "lucide-react";
import { SITES, TEAM } from "../data/content";

export const PartnerCTA = ({ t }) => (
  <section data-testid="partner-section" className="py-20 md:py-28 bg-copou-bg">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <Reveal>
        <div className="relative bg-copou-primary text-white p-10 md:p-16 overflow-hidden">
          <Newspaper size={220} className="absolute -right-10 -bottom-12 text-white/10" strokeWidth={1} />
          <div className="relative max-w-2xl">
            <Overline>{t.partner.overline}</Overline>
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight mt-4 text-white">{t.partner.title}</h2>
            <p className="mt-5 text-white/85 leading-relaxed text-base md:text-lg">{t.partner.text}</p>
            <a
              href={SITES.zda}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="partner-cta-zda"
              className="mt-8 inline-flex items-center gap-2 bg-white text-copou-primary px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-copou-bg transition-colors"
            >
              {t.partner.cta} <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export const AboutAgency = ({ t, lang }) => (
  <section id="about-agency" data-testid="about-agency-section" className="py-20 md:py-28 bg-copou-surface" itemScope itemType="https://schema.org/RealEstateAgent">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <Reveal className="max-w-3xl">
        <Overline>{t.agency.overline}</Overline>
        <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight mt-4 text-copou-ink" itemProp="name">{t.agency.title}</h2>
        <p className="mt-6 text-copou-muted leading-relaxed text-base md:text-lg" itemProp="description">{t.agency.p1}</p>
        <p className="mt-4 text-copou-muted leading-relaxed text-base md:text-lg">{t.agency.p2}</p>
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <a href={`tel:${SITES.phoneTel}`} className="inline-flex items-center gap-2 text-copou-primary font-medium" itemProp="telephone"><Phone size={15} /> {SITES.phone}</a>
          <a href={`mailto:${SITES.email}`} className="inline-flex items-center gap-2 text-copou-primary font-medium" itemProp="email"><Mail size={15} /> {SITES.email}</a>
          <a href={SITES.unu} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-copou-primary font-medium" itemProp="url"><ArrowUpRight size={15} /> unu-imobiliare.ro</a>
        </div>
      </Reveal>

      <Reveal delay={0.08} className="mt-10 bg-copou-bg border border-copou-line p-6 md:p-8">
        <div className="flex items-center gap-2 mb-3">
          <MapPin size={16} className="text-copou-primary" />
          <h3 className="font-serif text-xl text-copou-ink">{t.agency.zonesTitle}</h3>
        </div>
        <p className="text-copou-muted leading-relaxed" itemProp="areaServed">{t.agency.zonesText}</p>
      </Reveal>

      <Reveal delay={0.12} className="mt-12">
        <h3 className="font-serif text-2xl md:text-3xl font-light text-copou-ink mb-6">{t.agency.agentsTitle}</h3>
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
          {TEAM.map((m, i) => (
            <li key={i} data-testid={`agency-agent-${i}`} className="flex flex-col border-b border-copou-line/60 pb-3" itemProp="employee" itemScope itemType="https://schema.org/RealEstateAgent">
              <a href={m.url} target="_blank" rel="noopener noreferrer" className="font-serif text-lg text-copou-ink hover:text-copou-primary transition-colors" itemProp="url">
                <span itemProp="name">{m.name}</span>
              </a>
              <span className="text-sm text-copou-muted" itemProp="jobTitle">{m.role[lang]}</span>
              <a href={`tel:+4${m.phone.replace(/\s/g, "")}`} className="text-sm text-copou-primary mt-1 inline-flex items-center gap-1.5" itemProp="telephone"><Phone size={12} /> {m.phone}</a>
            </li>
          ))}
        </ul>
        <a
          href={SITES.team}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="about-agency-team-cta"
          className="mt-10 inline-flex items-center gap-2 border border-copou-primary text-copou-primary px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-copou-primary hover:text-white transition-colors"
        >
          {t.agency.cta} <ArrowUpRight size={16} />
        </a>
      </Reveal>
    </div>
  </section>
);

export const Footer = ({ t }) => (
  <footer data-testid="footer" className="bg-copou-ink text-white/80 pt-16 pb-8">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-12">
      <div>
        <div className="flex items-baseline gap-2">
          <span className="font-serif text-2xl text-white">UNU</span>
          <span className="text-xs uppercase tracking-[0.25em] text-copou-primary">Imobiliare</span>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-white/60 max-w-xs">{t.footer.tagline}</p>
      </div>

      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-copou-primary mb-4">{t.footer.links}</p>
        <ul className="space-y-2.5 text-sm">
          <li><a href={SITES.unu} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">unu-imobiliare.ro</a></li>
          <li><a href={SITES.unuOffers} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Oferte Iași</a></li>
          <li><a href={SITES.zda} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">ziaruldeapartamente.ro</a></li>
          <li><a href={SITES.copouGuide} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Ghid Copou 2026</a></li>
        </ul>
      </div>

      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-copou-primary mb-4">{t.footer.contact}</p>
        <ul className="space-y-2.5 text-sm">
          <li><a href={`tel:${SITES.phoneTel}`} className="inline-flex items-center gap-2 hover:text-white transition-colors"><Phone size={15} /> {SITES.phone}</a></li>
          <li><a href={`mailto:${SITES.email}`} className="inline-flex items-center gap-2 hover:text-white transition-colors"><Mail size={15} /> {SITES.email}</a></li>
          <li><a href={SITES.whatsapp} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">WhatsApp</a></li>
        </ul>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-6 md:px-12 mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-3 text-xs text-white/50">
      <span>© {new Date().getFullYear()} UNU Imobiliare · Copou, Iași</span>
      <span>{t.footer.made}</span>
    </div>
  </footer>
);
